import { z } from 'zod';
import type { Permission } from './admin.js';

/**
 * F61 (B17) — admin page KPI tiles (plans/F61_api_call_audit.md).
 *
 * The open-games, open-game-reports, tournaments, skill-disputes and clubs pages
 * and the dashboard used to read each tile through its (audited) list endpoint
 * with `perPage=1` — 2–5 heavy list queries and audit rows per page view.
 * `GET /admin/page-kpis?page=…` returns all of a page's tiles from one
 * `count(*) FILTER (…)` statement; it is not audited and is cached for 15 s.
 */

export const ADMIN_PAGE_KPI_PAGES = [
  'open_games',
  'open_game_reports',
  'tournaments',
  'skill_disputes',
  'clubs',
  'dashboard',
] as const;
export type AdminPageKpiPage = (typeof ADMIN_PAGE_KPI_PAGES)[number];

/** Each page's tiles. Dashboard parts are absent when the admin lacks their permission. */
export interface AdminPageKpisByPage {
  readonly open_games: {
    readonly open: number;
    readonly filled: number;
    readonly suspended: number;
    readonly reported: number;
  };
  readonly open_game_reports: {
    readonly open: number;
    readonly reviewed: number;
    readonly dismissed: number;
  };
  readonly tournaments: {
    readonly published: number;
    readonly registrationOpen: number;
    readonly live: number;
    readonly suspended: number;
  };
  readonly skill_disputes: { readonly open: number; readonly resolved: number };
  readonly clubs: {
    readonly active: number;
    readonly suspended: number;
    readonly verified: number;
  };
  readonly dashboard: {
    readonly venues?: { readonly total: number; readonly active: number; readonly flagged: number };
    readonly activeUsers?: number;
  };
}

export type AdminPageKpis<P extends AdminPageKpiPage = AdminPageKpiPage> = AdminPageKpisByPage[P];

/**
 * The permission each page's tiles need — the same gate as that page's list
 * route. The dashboard is gated per part (venues → `venues:read`, users →
 * `users:read`) and answers `{}` when the admin holds neither.
 */
export const ADMIN_PAGE_KPI_PERMISSIONS: Readonly<
  Record<Exclude<AdminPageKpiPage, 'dashboard'>, Permission>
> = {
  open_games: 'open_games:read',
  open_game_reports: 'open_games:read',
  tournaments: 'tournaments:read',
  skill_disputes: 'skill_disputes:resolve',
  clubs: 'clubs:read',
};

export const ADMIN_DASHBOARD_KPI_PERMISSIONS = {
  venues: 'venues:read',
  activeUsers: 'users:read',
} as const satisfies Record<keyof AdminPageKpisByPage['dashboard'], Permission>;

export const AdminPageKpisQuerySchema = z
  .object({
    page: z.enum(ADMIN_PAGE_KPI_PAGES),
    /** `1` skips the 15-s cache (a refresh after an admin action). */
    fresh: z.enum(['1']).optional(),
  })
  .strict();
export type AdminPageKpisQuery = z.infer<typeof AdminPageKpisQuerySchema>;
